// src/pages/EditRole.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";

// MUI
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { Autocomplete, TextField } from "@mui/material";

// MD Components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Layout
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Services & routes
import CrudService from "services/cruds-service";
import routes from "routes";
import { buildPageOptions } from "../pages";

export default function EditRole() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [pages, setPages] = useState([]);
  const [error, setError] = useState("");

  // derive all pages from routes.js
  const pagesOptions = useMemo(() => buildPageOptions(routes), []);

  // fetch existing role
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const res = await CrudService.getRole(id);
        const attrs = res.data.attributes;
        setName(attrs.name);
        // map allowedPages strings back to options
        setPages(
          pagesOptions.filter((opt) =>
            (attrs.allowedPages || []).includes(opt.value)
          )
        );
      } catch (err) {
        console.error(err);
      }
    })();
  }, [id, pagesOptions]);

  const submitHandler = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Role name is required");
      return;
    }
    const payload = {
      data: {
        type: "roles",
        id,
        attributes: {
          name: name.trim(),
          allowedPages: pages.map((p) => p.value),
        },
      },
    };
    try {
      await CrudService.updateRole(payload, id);
      navigate("/user/role-management", {
        state: { value: true, text: "Role succesfully updated" },
      });
    } catch (err) {
      console.error(err);
      setError(err?.errors?.[0]?.detail || "An error occurred");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar breadcrumbTitle={name} />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <MDBox mt={6} mb={4} textAlign="center">
              <MDTypography variant="h3" fontWeight="bold">
                Edit Role
              </MDTypography>
              <MDTypography variant="h5" color="secondary">
                This information will better define the role.
              </MDTypography>
            </MDBox>
            <Card>
              <MDBox component="form" onSubmit={submitHandler} p={3}>
                {/* Name */}
                <TextField
                  fullWidth
                  variant="standard"
                  label="Role Name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    setError("");
                  }}
                  margin="normal"
                />

                {/* Allowed Pages */}
                <Autocomplete
                  multiple
                  options={pagesOptions}
                  getOptionLabel={(opt) => opt.label}
                  value={pages}
                  onChange={(e, v) => {
                    setPages(v);
                    setError("");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label="Allowed Pages"
                      placeholder="Select pages"
                      margin="normal"
                    />
                  )}
                />

                {/* Error */}
                {error && (
                  <MDTypography variant="caption" color="error" display="block">
                    {error}
                  </MDTypography>
                )}

                {/* Buttons */}
                <MDBox display="flex" justifyContent="flex-end" mt={2}>
                  <MDButton
                    variant="gradient"
                    color="dark"
                    size="small"
                    onClick={() => navigate("/user/role-management")}
                    sx={{ mr: 1 }}
                  >
                    Back
                  </MDButton>
                  <MDButton variant="gradient" color="dark" size="small" type="submit">
                    Save
                  </MDButton>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
